import { isCallable } from '@afterpay/utils'
import clsx from 'clsx'
import React, { createRef, FunctionComponent, ReactNode, RefObject, useCallback, useContext, useEffect, useMemo, useRef } from 'react'

import { Icon } from '../Icon'
import { DesignLanguage, useIsDesignLanguage, useThemeClass } from '../Theme'
import { Text, TextComponent } from '../Typography'
import { useSwipeDetector } from '../useSwipeDetector'
import { KeyCodes } from '../utils/keyboard'
import Timer from '../utils/timer'
import { DEFAULT_TOAST_TIME, ITEM_KINDS, KINDS, TOAST_DELETE_DELAY, ToastLifecycle } from './constants'
import styles from './style.module.scss'
import { ToastIcon } from './ToastIcon'
import ToastLink from './ToastLink'
import { InternalToastOptions, ToastKinds, ToastState } from './types'

export const ToastContext = React.createContext<ToastState>({
  toastList: [],
  setToastList: () => {},
})

const toastTimerMap = new Map<string, Timer | null>()
const toastsTransitioningOut = new Map<string, boolean>()
interface ToastProps {
  kind?: ToastKinds
}
export interface ToastItemProps {
  onPause: (id: string) => () => void
  onResume: (id: string) => () => void
  setTransitionOut: (toast: InternalToastOptions, toastLifecycle?: ToastLifecycle) => void
  toastOpts: InternalToastOptions
  closeBtnRef: RefObject<HTMLButtonElement>
  toastRef: RefObject<HTMLDivElement>
}

const Headline: TextComponent = ({ children, size, ...props }) => {
  const isBronteDesign = useIsDesignLanguage(DesignLanguage.Bronte)
  const textSize = size ?? (isBronteDesign ? 'XS' : 'S')
  return (
    <Text {...props} size={textSize} bold>
      {children}
    </Text>
  )
}

const Body: TextComponent = ({ children, size, ...props }) => {
  const isBronteDesign = useIsDesignLanguage(DesignLanguage.Bronte)
  const textSize = size ?? (isBronteDesign ? 'XS' : 'S')
  return (
    <Text {...props} size={textSize}>
      {children}
    </Text>
  )
}

export const ToastItem: FunctionComponent<ToastItemProps> & { Headline: TextComponent; Body: TextComponent } = ({
  toastOpts,
  toastRef,
  closeBtnRef,
  setTransitionOut,
}) => {
  const themeClass = useThemeClass()
  const {
    message,
    linkLabel,
    linkUrl,
    manualDismiss,
    dismissContent,
    testNameSpace = '',
    id,
    swipeToDismiss = false,
    lifecycle,
    linkTarget = '_blank',
    hugContent = false,
    icon,
  } = toastOpts
  const kind = toastOpts.kind ?? ITEM_KINDS.DEFAULT
  const hasDismiss: boolean = typeof manualDismiss !== 'undefined' && manualDismiss
  const toastTestNameSpace = testNameSpace.length > 0 ? `${testNameSpace}-toast` : ''
  const toastItemContainer = clsx(
    styles.toastItemContainer,
    styles[`toastItem${kind}`],
    !toastTimerMap.has(id) && styles[lifecycle],
    styles[themeClass],
    hugContent && styles.hugContent
  )

  useSwipeDetector({
    onLeftSwiped: () => {
      if (swipeToDismiss) {
        setTransitionOut(toastOpts, ToastLifecycle.TRANSITION_OUT_LEFT)
      }
    },
    onRightSwiped: () => {
      if (swipeToDismiss) {
        setTransitionOut(toastOpts, ToastLifecycle.TRANSITION_OUT_RIGHT)
      }
    },
    onUpSwiped: () => {
      if (swipeToDismiss) {
        setTransitionOut(toastOpts, ToastLifecycle.TRANSITION_OUT_UP)
      }
    },
    domRef: toastRef,
  })

  const dismiss = dismissContent ?? <Icon.Close size='Small' title='Dismiss' />

  return (
    <div className={toastItemContainer} ref={toastRef} {...(toastTestNameSpace.length > 0 && { 'data-testid': toastTestNameSpace })}>
      <ToastIcon kind={kind} icon={icon} />
      <div className={styles.toastItem}>
        <Text testNameSpace={toastTestNameSpace} renderAs='div' className={styles.toastItemText}>
          <div className={hasDismiss ? styles.textMaxWidth : ''}>{isCallable(message) ? message() : <Body>{message as ReactNode}</Body>}</div>
        </Text>
        <ToastLink label={linkLabel} linkTarget={linkTarget} action={linkUrl} testNameSpace={toastTestNameSpace} />
        {hasDismiss && (
          <button
            ref={closeBtnRef}
            className={styles.closeButtonContainer}
            onClick={() => setTransitionOut(toastOpts)}
            {...(toastTestNameSpace.length > 0 && { 'data-testid': `${toastTestNameSpace}-close-btn` })}
          >
            {dismiss}
          </button>
        )}
      </div>
    </div>
  )
}

export const Toast: FunctionComponent<ToastProps> = ({ kind }) => {
  const toastContainerClasses = clsx(styles.toastContainer, kind === KINDS.STATIC && styles.static)
  const { toastList, setToastList } = useContext(ToastContext)
  const closeBtnRefs = useMemo(() => toastList.map(() => createRef<HTMLButtonElement>()), [toastList])
  const toastRefs = useMemo(() => toastList.map(() => createRef<HTMLDivElement>()), [toastList])
  const isMounted = useRef(false)

  useEffect(() => {
    isMounted.current = true
    toastList.forEach((toast) => {
      const isToastTimeZero = toast.toastTime === 0

      if (toastTimerMap.has(toast.id)) {
        return
      }

      if (toast.lifecycle === ToastLifecycle.TRANSITION_IN) {
        if (typeof toast.onShow !== 'undefined') {
          toast.onShow()
        }

        toastTimerMap.set(toast.id, isToastTimeZero ? null : new Timer(() => setTransitionOut(toast), toast.toastTime ?? DEFAULT_TOAST_TIME))
      } else if (
        toast.lifecycle === ToastLifecycle.TRANSITION_OUT ||
        toast.lifecycle === ToastLifecycle.TRANSITION_OUT_LEFT ||
        toast.lifecycle === ToastLifecycle.TRANSITION_OUT_RIGHT
      ) {
        toastTimerMap.set(toast.id, new Timer(() => dismissToast(toast), TOAST_DELETE_DELAY))
      }
    })

    return () => {
      isMounted.current = false
    }
  }, [toastList])

  const onPause = (id: string) => () => {
    toastTimerMap.get(id)?.pause()
  }

  const onResume = (id: string) => () => {
    toastTimerMap.get(id)?.resume()
  }

  const setTransitionOut = (toast: InternalToastOptions, toastLifecycle: ToastLifecycle = ToastLifecycle.TRANSITION_OUT): void => {
    // To ensure that toast only handles one transition out at a time
    if (toastsTransitioningOut.has(toast.id)) {
      return
    }

    toastsTransitioningOut.set(toast.id, true)

    toastTimerMap.delete(toast.id)

    if (isMounted.current) {
      setToastList((list: InternalToastOptions[]) => {
        return list.map((curr) => {
          if (curr.id !== toast.id) {
            return curr
          }

          return { ...toast, lifecycle: toastLifecycle }
        })
      })
    }
  }

  const dismissToast = (toast: InternalToastOptions): void => {
    toastsTransitioningOut.delete(toast.id)
    toastTimerMap.delete(toast.id)

    if (isMounted.current) {
      setToastList((list: InternalToastOptions[]) => {
        return list.filter((curr) => curr.id !== toast.id)
      })
    }

    if (typeof toast.onDismiss !== 'undefined') {
      toast.onDismiss()
    }
  }

  const handleKeyDown = useCallback(
    (evt: KeyboardEvent): void => {
      if ((evt.key as KeyCodes) === KeyCodes.Escape) {
        closeBtnRefs[0]?.current?.click()
      }
    },
    [closeBtnRefs]
  )

  const registerKeyDownListener = useCallback((): void => {
    window.addEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  const unregisterKeyDownListener = useCallback((): void => {
    window.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  useEffect(() => {
    // Taken from tooltip
    registerKeyDownListener()

    return () => {
      unregisterKeyDownListener()
    }
  }, [registerKeyDownListener, unregisterKeyDownListener])

  return (
    <div aria-live='polite' role='alert' className={toastContainerClasses}>
      {toastList.map((toast, i) => {
        const toastItemProps: ToastItemProps = {
          toastOpts: toast,
          setTransitionOut,
          onPause,
          onResume,
          closeBtnRef: closeBtnRefs[i],
          toastRef: toastRefs[i],
        }

        return <ToastItem key={i} {...toastItemProps} />
      })}
    </div>
  )
}

ToastItem.Headline = Headline
ToastItem.Body = Body
